import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import FileUploadIcon from "@/assets/svg/fileUpload.svg?react";
import { ViewingsDispatchRequest, ViewingsDispatchInspector } from "@/schemas";
import { FileText, Info, X } from "lucide-react";
import { useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  acceptOffer,
  declineAndRedispatch,
  simulateInvalidDateReply,
  submitViewingDate,
} from "../dispatchActions";
import { RequestUpdater } from "../types";

const OFFER_OPEN_STATUSES = ["contacted"];

export function InspectorUploadDialog({
  requestId,
  disabled,
  onUploadFiles,
}: {
  requestId: string;
  disabled?: boolean;
  onUploadFiles: (requestId: string, files: File[]) => Promise<number>;
}) {
  const intl = useIntl();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileCount, setUploadedFileCount] = useState(0);
  const [uploadError, setUploadError] = useState(false);
  const [lastSelectedFiles, setLastSelectedFiles] = useState<File[]>([]);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadDisabled = disabled || isUploading;

  const uploadFiles = async (files: File[]) => {
    setIsUploading(true);
    setUploadError(false);
    try {
      const uploadedCount = await onUploadFiles(requestId, files);
      setUploadedFileCount(uploadedCount);
      setLastSelectedFiles([]);
      setIsUploadDialogOpen(false);
    } catch {
      setUploadError(true);
      setUploadedFileCount(0);
    } finally {
      setIsUploading(false);
    }
  };
  const selectFiles = (files: File[]) => {
    if (files.length === 0) return;

    setUploadError(false);
    setLastSelectedFiles(files);
  };

  return (
    <div className="flex flex-col gap-2">
      <Dialog
        open={isUploadDialogOpen}
        onOpenChange={(open) => {
          setIsUploadDialogOpen(open);
          if (!open && !uploadError) setLastSelectedFiles([]);
        }}
      >
        <DialogTrigger asChild>
          <Button type="button" variant="secondary" disabled={uploadDisabled}>
            <FormattedMessage id="actions.upload" />
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-screen max-w-2xl overflow-auto">
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage id="actions.uploadFiles" />
            </DialogTitle>
            <DialogDescription className="sr-only">
              <FormattedMessage id="actions.uploadFilesDescription" />
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <div
                className="flex h-80 flex-1 flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/40 p-5 text-muted-foreground outline-none transition"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  selectFiles(Array.from(event.dataTransfer.files));
                }}
              >
                <Input
                  ref={fileInputRef}
                  aria-label={intl.formatMessage({ id: "actions.uploadFiles" })}
                  className="sr-only"
                  disabled={uploadDisabled}
                  multiple
                  type="file"
                  onChange={(event) => {
                    selectFiles(Array.from(event.target.files ?? []));
                    event.target.value = "";
                  }}
                />
                <div className="flex flex-col items-center justify-center gap-3">
                  <FileUploadIcon className="h-16" aria-hidden="true" />
                  <div className="flex flex-col items-center gap-1 text-center md:flex-row">
                    <p>
                      <FormattedMessage id="actions.dragAndDropFiles" />
                    </p>
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 underline"
                      disabled={uploadDisabled}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FormattedMessage id="actions.chooseFile" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
                <p>
                  <FormattedMessage id="actions.supportedFileFormats" />
                </p>
                <p>
                  <FormattedMessage id="actions.maxFileSize" />
                </p>
              </div>
            </div>

            <Card className="mx-auto w-full">
              <CardHeader className="flex flex-row items-center justify-between border-b border-border py-2">
                <CardTitle className="text-base font-medium text-foreground">
                  <FormattedMessage id="actions.uploadedDocuments" />
                </CardTitle>
                <Info
                  className="h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="py-4">
                <ul className="space-y-2">
                  {lastSelectedFiles.length > 0 ? (
                    lastSelectedFiles.map((file) => (
                      <li
                        key={`${file.name}-${file.size}-${file.lastModified}`}
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <FileText
                            className="h-4 w-4 shrink-0 text-foreground/60"
                            aria-hidden="true"
                          />
                          <p className="truncate text-foreground">
                            {file.name} - {file.size} bytes
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="icon"
                          variant="destructive"
                          className="h-9 w-9 shrink-0"
                          aria-label={intl.formatMessage(
                            { id: "actions.removeSelectedFile" },
                            { name: file.name },
                          )}
                          disabled={isUploading}
                          onClick={() => {
                            setLastSelectedFiles((files) =>
                              files.filter((item) => item !== file),
                            );
                          }}
                        >
                          <X className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </li>
                    ))
                  ) : (
                    <li className="text-sm text-muted-foreground">
                      <FormattedMessage id="actions.noFilesSelected" />
                    </li>
                  )}
                </ul>
              </CardContent>
            </Card>
          </div>

          {uploadError ? (
            <div className="flex flex-col gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive sm:flex-row sm:items-center sm:justify-between">
              <p>
                <FormattedMessage id="actions.uploadError" />
              </p>
              <Button
                type="button"
                variant="outline"
                disabled={isUploading || lastSelectedFiles.length === 0}
                onClick={() => uploadFiles(lastSelectedFiles)}
              >
                <FormattedMessage id="actions.retryUpload" />
              </Button>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isUploading}
              onClick={() => setIsUploadDialogOpen(false)}
            >
              <FormattedMessage id="actions.cancel" />
            </Button>
            <Button
              type="button"
              disabled={isUploading || lastSelectedFiles.length === 0}
              onClick={() => uploadFiles(lastSelectedFiles)}
            >
              {isUploading ? (
                <FormattedMessage id="actions.uploading" />
              ) : (
                <FormattedMessage id="actions.uploadProgress" />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {uploadedFileCount > 0 ? (
        <p className="text-sm text-muted-foreground">
          <FormattedMessage
            id="actions.uploadedFiles"
            values={{ count: uploadedFileCount }}
          />
        </p>
      ) : null}
    </div>
  );
}

export default function AssignmentActions({
  request,
  activeInspector,
  acceptDateInput,
  onAcceptDateInputChange,
  onUpdateRequest,
  onAcceptOffer,
  onDeclineOffer,
  onSubmitViewingDate,
  onUploadFiles,
  presentation = "default",
  showUpload = true,
}: {
  request: ViewingsDispatchRequest & { version?: number };
  activeInspector?: ViewingsDispatchInspector;
  acceptDateInput: string;
  onAcceptDateInputChange: (value: string) => void;
  onUpdateRequest: (requestId: string, updater: RequestUpdater) => void;
  onAcceptOffer?: (
    request: ViewingsDispatchRequest & { version?: number },
  ) => void;
  onDeclineOffer?: (
    request: ViewingsDispatchRequest & { version?: number },
  ) => void;
  onSubmitViewingDate?: (
    request: ViewingsDispatchRequest & { version?: number },
    scheduledFor: string,
  ) => void;
  onUploadFiles: (requestId: string, files: File[]) => Promise<number>;
  presentation?: "default" | "decision";
  showUpload?: boolean;
}) {
  const intl = useIntl();
  const isAssignedToCurrentInspector =
    request.assignedInspectorId === activeInspector?.id;
  const offerIsOpen = OFFER_OPEN_STATUSES.includes(request.lifecycleStatus);
  const canSubmitDate = request.lifecycleStatus === "accepted";
  const canSimulateInvalidDateReply = request.lifecycleStatus === "accepted";
  const canUpload = showUpload && request.lifecycleStatus === "scheduled";
  const assignmentInvalidated = Boolean(
    request.outreach?.isAssignmentInvalidated,
  );

  if (assignmentInvalidated) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        <FormattedMessage id="actions.assignmentInvalidated" />
      </div>
    );
  }

  const decisionButtonClassName =
    presentation === "decision"
      ? "h-10 px-4 text-sm font-semibold shadow-sm"
      : undefined;
  const actionContainerClassName =
    presentation === "decision"
      ? "flex flex-wrap gap-2"
      : "grid gap-3 md:grid-cols-2";
  return (
    <div className={actionContainerClassName}>
      {offerIsOpen ? (
        <>
          <Button
            className={decisionButtonClassName}
            disabled={!isAssignedToCurrentInspector}
            onClick={() => {
              if (!activeInspector) return;

              if (onAcceptOffer) {
                onAcceptOffer(request);
                return;
              }

              onUpdateRequest(request.id, (current) =>
                acceptOffer(current, activeInspector.name),
              );
            }}
          >
            <FormattedMessage id="actions.accept" />
          </Button>

          <Button
            variant="outline"
            className={
              presentation === "decision"
                ? `${decisionButtonClassName} border-destructive text-destructive hover:bg-background hover:text-destructive`
                : decisionButtonClassName
            }
            disabled={!isAssignedToCurrentInspector}
            onClick={() => {
              if (!activeInspector) return;

              if (onDeclineOffer) {
                onDeclineOffer(request);
                return;
              }

              onUpdateRequest(request.id, (current) =>
                declineAndRedispatch(current, activeInspector.name),
              );
            }}
          >
            <FormattedMessage id="actions.decline" />
          </Button>
        </>
      ) : null}

      {canSubmitDate ? (
        <div className="flex flex-col gap-2 md:col-span-2 sm:flex-row">
          <Input
            type="date"
            aria-label={intl.formatMessage({ id: "actions.viewingDate" })}
            value={acceptDateInput}
            onChange={(event) => onAcceptDateInputChange(event.target.value)}
          />
          <Button
            disabled={!isAssignedToCurrentInspector}
            onClick={() => {
              if (!activeInspector) return;

              if (onSubmitViewingDate) {
                onSubmitViewingDate(request, acceptDateInput);
                return;
              }

              onUpdateRequest(request.id, (current) =>
                submitViewingDate(
                  current,
                  activeInspector.name,
                  acceptDateInput,
                ),
              );
            }}
          >
            <FormattedMessage id="actions.submitDate" />
          </Button>
          {canSimulateInvalidDateReply ? (
            <Button
              type="button"
              variant="outline"
              disabled={!isAssignedToCurrentInspector}
              onClick={() => {
                if (!activeInspector) return;

                onUpdateRequest(request.id, (current) =>
                  simulateInvalidDateReply(current, activeInspector.name),
                );
              }}
            >
              <FormattedMessage id="actions.simulateInvalidDate" />
            </Button>
          ) : null}
        </div>
      ) : null}

      {canUpload ? (
        <div className="md:col-span-2">
          <InspectorUploadDialog
            requestId={request.id}
            disabled={!isAssignedToCurrentInspector}
            onUploadFiles={onUploadFiles}
          />
        </div>
      ) : null}
    </div>
  );
}
